/**
 * DOOR HARDWARE round (2026-10-09, Matt: "Take a look at the hardware for doors and update their
 * look. Right now they are primitive bad looking shapes. Browse the Emtek catalogue, the Baldwin
 * catalogue, and the Rocky Mountain Hardware catalogue for inspiration … Add the option in our
 * style starter packs under templates to select default hardware … levers, deadbolts, ball
 * catches, etc. And should we include hinges on the doors that swing?"). Docs: docs/MODELER.md
 * "DOOR HARDWARE ROUND".
 *
 * Two independent choices, the panel-style pattern again:
 *   - the LOOK — one family of rose/backplate + lever + knob shapes + a default finish, picked per
 *     project (Project Settings / the style pack) with a per-door override;
 *   - the PIECES — what the door's hardware SET puts on it (the firm's catalog sets, by name):
 *     levers or a handleset, a deadbolt, a privacy turn, a ball catch, flush bolts on a pair's
 *     inactive leaf, edge pulls on a pocket door, and hinges counted by the leaf's height.
 *
 * Dimensions are from the makers' published spec sheets (Emtek rosettes + Helios/Hercules levers,
 * Baldwin Estate 5485V/5455V/5048, Rocky Mountain Hardware's 2026 passage catalog) — the looks are
 * INSPIRED by those families, never their product names. `resolveDoorHardware` is the one reading
 * every consumer shares: the 3D prototype, the elevation figure, the picture cards and the schedule.
 */

const IN = 1 / 12

export type HardwareLook = 'modernRect' | 'narrowPlate' | 'contemporarySquare' | 'traditional' | 'rusticBronze' | 'metroPlate'
export type RoseShape = 'rect' | 'round' | 'arched'
export type LeverShape = 'bar' | 'squareBar' | 'curved' | 'beaverTail' | 'blade'
export type KnobShape = 'round' | 'egg'

export interface HardwareLookSpec {
  value: HardwareLook
  label: string
  /** the family it takes after (shown as a hint, never as the product's name) */
  hint: string
  /** the rose or backplate behind the lever: shape + width x height + thickness */
  rose: { shape: RoseShape; w: number; h: number; t: number }
  /** a backplate tall enough to carry the deadbolt above the lever (the steel-door / plate looks) */
  plate: boolean
  lever: LeverShape
  /** lever length from the spindle, and how far it stands off the door */
  leverLen: number
  leverProj: number
  knob: KnobShape
  finish: DoorHardwareFinish
}

export type DoorHardwareFinish = 'flatBlack' | 'satinNickel' | 'polishedChrome' | 'satinBrass' | 'unlacqueredBrass' | 'oilRubbedBronze' | 'darkBronze'

export const DOOR_HARDWARE_FINISHES: { value: DoorHardwareFinish; label: string; hex: string }[] = [
  { value: 'flatBlack', label: 'Flat Black', hex: '#2b2b2a' },
  { value: 'satinNickel', label: 'Satin Nickel', hex: '#a9aaa6' },
  { value: 'polishedChrome', label: 'Polished Chrome', hex: '#cdd0d2' },
  { value: 'satinBrass', label: 'Satin Brass', hex: '#bfa064' },
  { value: 'unlacqueredBrass', label: 'Unlacquered Brass', hex: '#a9864c' },
  { value: 'oilRubbedBronze', label: 'Oil-Rubbed Bronze', hex: '#3a2b21' },
  { value: 'darkBronze', label: 'Dark Bronze', hex: '#4d3c2e' },
]
export const isDoorHardwareFinish = (v: unknown): v is DoorHardwareFinish => DOOR_HARDWARE_FINISHES.some((f) => f.value === v)
export const doorHardwareFinishHex = (v: unknown): string => DOOR_HARDWARE_FINISHES.find((f) => f.value === v)?.hex ?? '#2b2b2a'
export const doorHardwareFinishLabel = (v: unknown): string => DOOR_HARDWARE_FINISHES.find((f) => f.value === v)?.label ?? ''

export const HARDWARE_LOOKS: HardwareLookSpec[] = [
  {
    value: 'modernRect', label: 'Modern Rectangular', hint: 'Square-edged rectangular rose, straight bar lever (after Emtek Modern Rectangular)',
    rose: { shape: 'rect', w: 2.5 * IN, h: 4 * IN, t: 0.375 * IN }, plate: false,
    lever: 'bar', leverLen: 5 * IN, leverProj: 2 * IN, knob: 'round', finish: 'flatBlack',
  },
  {
    value: 'narrowPlate', label: 'Narrow Backplate', hint: 'A 1-1/2" x 11" plate carrying lever and deadbolt, slim lever — the steel-door look (after Emtek Stretto, RMH Edge)',
    rose: { shape: 'rect', w: 1.5 * IN, h: 11 * IN, t: 0.375 * IN }, plate: true,
    lever: 'bar', leverLen: 5 * IN, leverProj: 2.0625 * IN, knob: 'round', finish: 'flatBlack',
  },
  {
    value: 'contemporarySquare', label: 'Contemporary Square', hint: '2-1/2" square rose, square-section lever (after Baldwin Soho)',
    rose: { shape: 'rect', w: 2.5 * IN, h: 2.5 * IN, t: 0.375 * IN }, plate: false,
    lever: 'squareBar', leverLen: 4.375 * IN, leverProj: 2.125 * IN, knob: 'round', finish: 'satinNickel',
  },
  {
    value: 'traditional', label: 'Traditional', hint: 'Round 2-5/8" rose, gently curved lever (after Baldwin Estate and Emtek brass)',
    rose: { shape: 'round', w: 2.625 * IN, h: 2.625 * IN, t: 0.375 * IN }, plate: false,
    lever: 'curved', leverLen: 4 * IN, leverProj: 2.5 * IN, knob: 'egg', finish: 'unlacqueredBrass',
  },
  {
    value: 'rusticBronze', label: 'Rustic Bronze', hint: 'Cast arched rose, beaver-tail lever (after Rocky Mountain Hardware)',
    rose: { shape: 'arched', w: 2.5 * IN, h: 4.5 * IN, t: 0.4375 * IN }, plate: false,
    lever: 'beaverTail', leverLen: 4.75 * IN, leverProj: 2.25 * IN, knob: 'round', finish: 'darkBronze',
  },
  {
    value: 'metroPlate', label: 'Metro Plate', hint: 'A 2-1/4" x 10" rectangular plate, flat blade lever (after Rocky Mountain Metro)',
    rose: { shape: 'rect', w: 2.25 * IN, h: 10 * IN, t: 0.375 * IN }, plate: true,
    lever: 'blade', leverLen: 4.75 * IN, leverProj: 2.125 * IN, knob: 'round', finish: 'satinNickel',
  },
]
export const isHardwareLook = (v: unknown): v is HardwareLook => HARDWARE_LOOKS.some((l) => l.value === v)
export const hardwareLookSpec = (v: unknown): HardwareLookSpec => HARDWARE_LOOKS.find((l) => l.value === v) ?? HARDWARE_LOOKS[0]
export const hardwareLookLabel = (v: unknown): string => HARDWARE_LOOKS.find((l) => l.value === v)?.label ?? ''

/** the look a door gets when nothing sets one — steel doors take the narrow backplate (Euroline) */
export function defaultHardwareLook(frameSystem: unknown): HardwareLook {
  return frameSystem === 'steel' ? 'narrowPlate' : 'modernRect'
}

/** what a handle on one face of the leaf is */
export type HandleKind = 'lever' | 'knob' | 'ladderPull' | 'handleset' | 'none'

/** what the firm's hardware SETS put on a door, by set name (the catalog's own vocabulary) */
export interface SetPieces {
  deadbolt?: boolean
  privacy?: boolean
  handleset?: boolean
  ballCatch?: boolean
  dummy?: boolean
  pivots?: boolean
  edgePull?: boolean
  thumbTurn?: boolean
}
const SET_PIECES: [RegExp, SetPieces][] = [
  [/custom pivot/i, { deadbolt: true, pivots: true }],
  [/dutch entry/i, {}],
  [/garage entry/i, {}],
  [/exterior utility/i, { deadbolt: true }],
  [/^entry/i, { deadbolt: true, handleset: true }],
  [/pocket bath/i, { privacy: true, edgePull: true }],
  [/pocket/i, { edgePull: true }],
  [/bath|bedroom|office/i, { privacy: true }],
  [/shallow coat/i, { ballCatch: true, dummy: true }],
  [/multi-slide|patio/i, { thumbTurn: true }],
]
export function hardwareSetPieces(setName: unknown): SetPieces {
  const n = typeof setName === 'string' ? setName : ''
  for (const [re, p] of SET_PIECES) if (re.test(n)) return p
  return {}
}

/** hinges on a swinging leaf of this height: 2 up to 5', 3 up to 7'-6", then one more each 30" */
export function hingeCount(leafHeightFt: number): number {
  if (leafHeightFt <= 5) return 2
  if (leafHeightFt <= 7.5) return 3
  return 3 + Math.ceil((leafHeightFt - 7.5) / 2.5)
}
/** hinge centers on a leaf from its bottom: 11" up, 7" down from the top, the rest evenly between */
export function hingeStations(z0: number, z1: number, n: number, hingeH = 4 * IN): number[] {
  const lo = z0 + 11 * IN + hingeH / 2, hi = z1 - 7 * IN - hingeH / 2
  if (n <= 1 || hi <= lo) return [Math.max(z0, (z0 + z1) / 2)]
  return Array.from({ length: n }, (_, i) => lo + ((hi - lo) * i) / (n - 1))
}

const SWING_OPS = ['hinged', 'french', 'dutch']

export interface DoorHardwareKit {
  look: HardwareLook
  finish: DoorHardwareFinish
  hex: string
  exterior: HandleKind
  interior: HandleKind
  deadbolt: boolean
  privacy: boolean
  /** hinges per swinging leaf (0 = none drawn) */
  hinges: number
  pivots: boolean
  ballCatch: boolean
  /** a pair's inactive leaf: dummy levers + flush bolts top and bottom + ball catch */
  pairInactive: boolean
  edgePull: boolean
  thumbTurn: boolean
}

/**
 * The door's hardware: its set's pieces on its own handle choices, in its look + finish.
 * `params` are the door's RESOLVED params (handles, set, height, construction), `op` its operation.
 */
export function resolveDoorHardware(
  params: Record<string, unknown>, op: string,
  look: HardwareLook, finish: DoorHardwareFinish,
): DoorHardwareKit {
  const setName = params.hardwareGroup
  const p = hardwareSetPieces(setName)
  const asKind = (v: unknown): HandleKind => (v === 'knob' || v === 'ladderPull' || v === 'none' ? v : 'lever')
  let ext = asKind(params.handleExterior), int = asKind(params.handleInterior)
  if (p.handleset && ext === 'lever') ext = 'handleset'
  const h = (params.heightFt as number) || 6 + 8 / 12
  return {
    look, finish, hex: doorHardwareFinishHex(finish),
    exterior: ext, interior: int,
    deadbolt: !!p.deadbolt,
    privacy: !!p.privacy,
    hinges: SWING_OPS.includes(op) ? hingeCount(op === 'dutch' ? h / 2 + 0.01 : h) : 0,
    pivots: op === 'pivot' || !!p.pivots,
    ballCatch: !!p.ballCatch || op === 'french',
    pairInactive: op === 'french',
    edgePull: !!p.edgePull || op === 'pocket',
    thumbTurn: !!p.thumbTurn,
  }
}

/** the schedule's one-line hardware description */
export function hardwareSummary(k: DoorHardwareKit): string {
  const parts: string[] = []
  const handle = (x: HandleKind) => (x === 'handleset' ? 'Handleset' : x === 'ladderPull' ? 'Pull' : x === 'knob' ? 'Knob' : x === 'lever' ? 'Lever' : '')
  const e = handle(k.exterior), i = handle(k.interior)
  if (e || i) parts.push(e === i || !i ? e : !e ? i : `${e} / ${i}`)
  if (k.deadbolt) parts.push('Deadbolt')
  if (k.privacy) parts.push('Privacy')
  if (k.thumbTurn) parts.push('Thumb Turn')
  if (k.hinges) parts.push(`${k.hinges} Hinges`)
  if (k.pivots) parts.push('Pivots')
  if (k.pairInactive) parts.push('Flush Bolts')
  if (k.ballCatch) parts.push('Ball Catch')
  if (k.edgePull) parts.push('Edge Pull')
  return parts.join(', ')
}
